import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import { FiX, FiChevronLeft, FiChevronRight, FiImage } from 'react-icons/fi';
import { settingsAPI } from '../../services/api';
import { resolveImageUrl } from '../../utils/helpers';
import { getFacilityIcon, getFacilityColorClasses } from '../../utils/facilityOptions';

export default function FacilitiesSection() {
  const { ref, isVisible } = useScrollAnimation();
  const [facilities, setFacilities] = useState([]);
  const [activeFacility, setActiveFacility] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(0);

  useEffect(() => {
    settingsAPI
      .get()
      .then((res) => {
        setFacilities(res.data?.data?.settings?.facilities || []);
      })
      .catch(() => {});
  }, []);

  const activeImages =
    activeFacility !== null ? facilities[activeFacility]?.images || [] : [];

  const openGallery = (idx) => {
    const imgs = facilities[idx]?.images || [];
    if (imgs.length === 0) return;
    setActiveFacility(idx);
    setLightboxIdx(0);
  };
  const closeGallery = () => setActiveFacility(null);
  const prevImage = () =>
    setLightboxIdx((i) => (i > 0 ? i - 1 : activeImages.length - 1));
  const nextImage = () =>
    setLightboxIdx((i) => (i < activeImages.length - 1 ? i + 1 : 0));

  useEffect(() => {
    const handleKey = (e) => {
      if (activeFacility === null) return;
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'Escape') closeGallery();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [activeFacility, activeImages.length]);

  if (facilities.length === 0) return null;

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 sm:mb-14"
        >
          <p className="section-subtitle mb-2">World-Class Facilities</p>
          <h2 className="section-title mb-4">State-of-the-Art Infrastructure</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-sm sm:text-base">
            We provide excellent facilities to create an optimal learning environment
            that supports academic and personal growth.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5">
          {facilities.map((facility, idx) => {
            const imgs = facility.images || [];
            const hasImages = imgs.length > 0;
            const Icon = getFacilityIcon(facility.icon);
            return (
              <motion.div
                key={facility.key}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={isVisible ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.45, delay: idx * 0.07 }}
                onClick={() => openGallery(idx)}
                className={`relative overflow-hidden bg-white border border-gray-100 rounded-2xl p-4 sm:p-5 group card-hover shadow-sm ${hasImages ? 'cursor-pointer' : 'cursor-default'}`}
              >
                {hasImages && (
                  <>
                    <span className="absolute top-2 right-2 z-10 flex items-center gap-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                      <FiImage className="w-2.5 h-2.5" /> {imgs.length}
                    </span>
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-[5]">
                      <img
                        src={resolveImageUrl(imgs[0].url)}
                        alt={facility.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
                        <span className="text-white text-xs font-medium flex items-center gap-1 text-center px-2">
                          <FiImage className="w-3.5 h-3.5 flex-shrink-0" />
                          View {imgs.length} photo{imgs.length > 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  </>
                )}
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center mb-3 sm:mb-4 border group-hover:scale-110 transition-transform ${getFacilityColorClasses(facility.color)}`}>
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <h3 className="font-bold text-gray-800 text-xs sm:text-sm mb-1.5 sm:mb-2">{facility.title}</h3>
                <p className="text-gray-400 text-xs leading-relaxed hidden sm:block">{facility.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Gallery Lightbox */}
      <AnimatePresence>
        {activeFacility !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={closeGallery}
          >
            <button
              onClick={closeGallery}
              className="absolute top-4 right-4 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors z-10"
            >
              <FiX className="w-6 h-6" />
            </button>

            {activeImages.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 rounded-full hover:bg-white/10 transition-colors z-10"
                >
                  <FiChevronLeft className="w-7 h-7" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 rounded-full hover:bg-white/10 transition-colors z-10"
                >
                  <FiChevronRight className="w-7 h-7" />
                </button>
              </>
            )}

            <motion.div
              key={lightboxIdx}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-4xl w-full"
            >
              <img
                src={resolveImageUrl(activeImages[lightboxIdx]?.url)}
                alt={facilities[activeFacility]?.title}
                className="w-full max-h-[75vh] object-contain rounded-2xl"
              />
              <div className="text-center mt-4">
                <h3 className="text-white font-semibold">{facilities[activeFacility]?.title}</h3>
              </div>
              {activeImages.length > 1 && (
                <div className="text-center mt-3 text-white/40 text-sm">
                  {lightboxIdx + 1} / {activeImages.length}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
